import type { TranscriptFlagItem } from "@rosc/contracts";

// Helpers for the AI flags on the transcript (v3.2): flags carry offsets in
// the whole transcript text; the transcript is shown as speaker turns, so
// each turn finds the flags inside it.

export type FlagPiece = { text: string; flag: TranscriptFlagItem | null };

// Splits `text` into plain and flagged pieces. `flags` use offsets in `text`
// (overlaps keep the first flag).
export function splitFlagged(
  text: string,
  flags: readonly Pick<TranscriptFlagItem, "start" | "end">[] &
    readonly TranscriptFlagItem[],
): FlagPiece[] {
  const sorted = [...flags]
    .filter((flag) => flag.start < flag.end && flag.end <= text.length)
    .sort((a, b) => a.start - b.start);
  const pieces: FlagPiece[] = [];
  let at = 0;
  for (const flag of sorted) {
    if (flag.start < at) continue;
    if (flag.start > at)
      pieces.push({ text: text.slice(at, flag.start), flag: null });
    pieces.push({ text: text.slice(flag.start, flag.end), flag });
    at = flag.end;
  }
  if (at < text.length) pieces.push({ text: text.slice(at), flag: null });
  return pieces;
}

// The flags of one turn, with offsets in the turn's text. The turn is found
// in the raw transcript after `cursor` (turns are in order); a flag outside
// it is left for another turn. When the turn's text is not a plain slice of
// the transcript (a speaker's turn spread over several labels), each flag's
// quote is looked up in the turn instead.
export function flagsInTurn(
  raw: string,
  turnText: string,
  cursor: number,
  flags: readonly TranscriptFlagItem[],
): { flags: TranscriptFlagItem[]; next: number } {
  const at = raw.indexOf(turnText, cursor);
  if (at >= 0) {
    const end = at + turnText.length;
    return {
      flags: flags
        .filter((flag) => flag.start >= at && flag.end <= end)
        .map((flag) => ({
          ...flag,
          start: flag.start - at,
          end: flag.end - at,
        })),
      next: end,
    };
  }
  const found: TranscriptFlagItem[] = [];
  for (const flag of flags) {
    if (flag.start < cursor) continue;
    const start = turnText.indexOf(flag.quote);
    if (start >= 0)
      found.push({ ...flag, start, end: start + flag.quote.length });
  }
  return { flags: found, next: cursor };
}
